import type React from 'react'
import type { Offset } from 'maplibre-gl'
import { useMap, type LngLatBounds } from 'react-map-gl/maplibre'

import Popup from '@/components/map/popup'

import { popup } from './maplibre-popup.css'

// Anchor type to match react-map-gl
type AnchorType =
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

// Function to calculate anchor position based on marker location relative to bounds
type AnchorRule = [/* condition */ boolean, /* result */ AnchorType]

const calculateAnchor = (
  markerLat: number,
  markerLng: number,
  bounds: LngLatBounds
): AnchorType => {
  const { lat: north, lng: west } = bounds.getNorthWest()
  const { lat: south, lng: east } = bounds.getSouthEast()

  // get percentages of the marker's position relative to the bounds
  const yPct = (north - markerLat) / (north - south)
  const xPct = (markerLng - west) / (east - west)

  // X and Y coordinate thresholds
  const Y_CENTER = 0.4
  const Y_CENTERWithX_CENTER = 0.26
  const X_CENTER = 0.3

  const nearTop = yPct <= Y_CENTER
  const nearBottom = yPct >= 1 - Y_CENTER
  const nearLeft = xPct <= X_CENTER
  const nearRight = xPct >= 1 - X_CENTER
  const nearCenter = !nearLeft && !nearRight && !nearTop && !nearBottom
  const nearTopXCenter = yPct < Y_CENTERWithX_CENTER
  const nearBottomXCenter = yPct > 1 - Y_CENTERWithX_CENTER

  const rules: AnchorRule[] = [
    [nearCenter && xPct < 0.5 && nearTopXCenter, 'top-left'],
    [nearCenter && xPct < 0.5 && nearBottomXCenter, 'bottom-left'],
    [nearCenter && xPct < 0.5, 'left'],
    [nearCenter && xPct >= 0.5 && nearTopXCenter, 'top-right'],
    [nearCenter && xPct >= 0.5 && nearBottomXCenter, 'bottom-right'],
    [nearCenter && xPct >= 0.5, 'right'],
    [nearTop && nearLeft, 'top-left'],
    [nearTop && nearRight, 'top-right'],
    [nearBottom && nearLeft, 'bottom-left'],
    [nearBottom && nearRight, 'bottom-right'],
    [nearTop, 'top'],
    [nearBottom, 'bottom'],
    [nearLeft, 'left'],
    [nearRight, 'right']
  ]

  for (const [cond, anchor] of rules) if (cond) return anchor
  return 'bottom'
}

type Props = React.PropsWithChildren & {
  latitude: number
  longitude: number
  closeOnMove?: boolean
  offset?: Offset
}
const MaplibrePopup: React.FC<Props> = ({
  latitude,
  longitude,
  children,
  closeOnMove,
  offset = {
    top: [0, 10],
    bottom: [0, -25],
    right: [-35, 0],
    left: [35, 0],
    'top-left': [35, -5],
    'top-right': [-35, -5],
    'bottom-right': [-35, -5],
    'bottom-left': [35, 0]
  } as Offset
}) => {
  const { current: map } = useMap()

  const getInitialAnchor = (): AnchorType => {
    if (!map) return 'bottom'
    try {
      const bounds = map.getBounds()
      return calculateAnchor(latitude, longitude, bounds)
    } catch {
      return 'bottom'
    }
  }

  const anchor = getInitialAnchor()

  return (
    <Popup
      latitude={latitude}
      longitude={longitude}
      closeOnMove={closeOnMove}
      className={popup}
      maxWidth="318px"
      closeButton={false}
      offset={offset}
      anchor={anchor}
    >
      {children}
    </Popup>
  )
}
export default MaplibrePopup
